<template>
  <div class="campaigns-jobs-index">
    <div v-if="!hasContentEnabled" class="alert alert-warning">
      <b-row>
        <b-col cols="12" class="d-flex justify-content-center align-items-center">
          <p>
            <b>The Content feature is not enabled in your platform.</b>
          </p>
        </b-col>
        <b-col v-if="isRecruiterAdmin" cols="12" class="d-flex justify-content-center align-items-center">
          <b-button variant="primary" href="mailto:sales@reallinks.io?subject=Enabling Content in our platform">Contact Sales to enable this feature</b-button>
        </b-col>
      </b-row>
    </div>
    <div v-else>
    <b-row v-if="showStepper" class="fixed-top bg-white border-bottom">
      <b-col class="py-3">
        <onboarding-stepper
          :options="{
            paddingBottom: false,
            borderBottom: false,
          }"
          :currentStep="currentStep"
          :steps="steps"
        />
      </b-col>
    </b-row>
    <!-- Header -->
    <b-row class="my-3">
      <b-col cols="12" sm="7">
        <h2>{{ $t('contentShares.title') }}</h2>
        <p v-if="isRecruiter">{{ $t('contentShares.description-recruiter') }}</p>
        <p v-else>{{ $t('contentShares.description-employee') }}</p>
      </b-col>
      <b-col cols="12" sm="5" class="d-flex flex-row justify-content-end align-items-center">
        <!-- Add Content by URL Button -->
        <b-button v-if="isRecruiterAdmin && hasContentEditorV2Enabled" size="md" variant="outline-primary" v-b-modal.new-content-modal>
          <font-awesome-icon icon="plus"/>
          <span class="d-none d-sm-inline-block">{{ $t('content-fetch.buttons.new-content') }}</span>
        </b-button>
        <b-button v-else-if="isRecruiterAdmin" size="md" @click.prevent="() => selectedContentShareID = 'NEW'" variant="outline-primary" v-b-modal.new-content-by-url-modal>
          <font-awesome-icon icon="plus"/>
          <span class="d-none d-sm-inline-block">{{ $t('content-fetch.buttons.new-content') }}</span>
        </b-button>
      </b-col>
    </b-row>

    <!-- Error -->
    <error-block v-if="allContentSharesErr" class="mb-3" :err="allContentSharesErr"/>

    <!-- Add Content Modal -->
    <b-modal
      v-if="isRecruiterAdmin && hasContentEditorV2Enabled"
      id="new-content-modal"
      size="xl"
      title="Create content share"
      no-close-on-backdrop
      hide-footer
    >
      <content-share-editor
        @created="newContentCreated"
      />
    </b-modal>

    <!-- Edit Content Modal -->
    <b-modal
      v-if="isRecruiterAdmin && hasContentEditorV2Enabled"
      id="edit-content-modal"
      size="xl"
      title="Update content share"
      no-close-on-backdrop
      hide-footer
    >
      <content-share-editor
        :loading="$apollo.queries.selectedContentShare.loading"
        :value="selectedContentShare"
        @updated="contentUpdated"
      />
    </b-modal>

    <!-- Add Content Modal - Legacy -->
    <template v-if="isRecruiterAdmin && selectedContentShareID && !hasContentEditorV2Enabled">
      <b-modal
        v-if="selectedContentShare && selectedContentShare.id"
        id="new-content-by-url-modal"
        visible
        size="xl"
        :title="contentShareEditorModalTitle"
        no-close-on-backdrop
        hide-footer
        @hidden="onShareModalClosed"
      >
        <error-block
          v-if="selectedContentShareErr" class="mb-3" :err="selectedContentShareErr"
        />
        <content-share-fetch
          :loading="$apollo.queries.prefetchedContentShare.loading"
          :prefetchError="prefetchError"
          :value="{
            url: prefetchURL || selectedContentShare.url,
            ...(prefetchedContentShare || (({ url, ...r }) => r)(selectedContentShare || {})),
          }"
          @fetchContent ="fetchContent($event)"
          @dismissPrefetchError="prefetchError = null"
          @input="updateContentShare"
          @disableSelectedContentShare="disableSelectedContentShare"
          @publishSelectedContentShare="publishSelectedContentShare"
          @deleteSelectedContentShare="deleteSelectedContentShare"
        />
      </b-modal>
      <b-modal
        v-else-if="selectedContentShareID === 'NEW'"
        visible
        size="xl"
        :title="contentShareEditorModalTitle"
        no-close-on-backdrop
        hide-footer
        @hidden="onShareModalClosed"
      >
        <error-block
          v-if="selectedContentShareErr"
          class="mb-3"
          :err="selectedContentShareErr"
        />
        <content-share-fetch
          :loading="$apollo.queries.prefetchedContentShare.loading"
          :prefetchError="prefetchError"
          :value="{
            url: prefetchURL,
            ...(prefetchedContentShare || {}),
          }"
          @fetchContent="fetchContent($event)"
          @dismissPrefetchError="prefetchError = null"
          @input="createNewContentShare($event)"
        />
      </b-modal>
    </template>

    <!-- Content -->
    <filter-container cols="3" :loading="$apollo.loading || !api" show-filters
      :results="api ? api.contentShareConnections : null" @pageUpdated="filterPageUpdated"
      :order="filterOrder" @orderUpdated="filterOrderUpdated" :orderByEnums="orderByEnums">
        <template v-slot:where>
          <filter-content-shares-where :campaigns="api ? api.campaigns : []" :value="filterWhere" @input="filterWhereUpdated"/>
        </template>
        <template v-slot:before>
          <schedule-linkedin-card v-if="canScheduleLinkedInPosts" class="my-3" scheduleModalID="schedule-campaign-content">
            <template v-slot:schedule-posts-modal>
              <schedule-posts-modal
                :contentShareIDs="api && Array.isArray(api.contentShareConnections.edges) ? api.contentShareConnections.edges.map(({ entry }) => entry.id) : null"
                @modal-close="() => $bvModal.hide('schedule-campaign-content')"/>
            </template>
          </schedule-linkedin-card>
        </template>
        <template v-slot:item="{ entry }">
          <content-card :entry="entry" @trackShare="data => trackShareEvent(entry, data)">
            <template v-slot:footer v-if="isRecruiter && isEnglishLang">
              <b-card-footer class="d-flex align-items-center justify-content-between">
                <div>
                  <b-btn
                    variant="link"
                    @click="openEditModal(entry.id)"
                  >
                    Edit
                  </b-btn>
                  <b-btn
                    v-if="entry.status === 'ACTIVE'"
                    class="ml-2"
                    variant="link"
                    @click="updateStatus(entry.id, 'DELETED')"
                  >
                    Archive
                  </b-btn>
                  <b-btn
                    v-if="entry.status === 'DELETED'"
                    class="ml-2"
                    variant="link"
                    @click="updateStatus(entry.id, 'ACTIVE')"
                  >
                    Restore
                  </b-btn>
                </div>
                <div v-if="entry.status === 'ACTIVE'"> ACTIVE </div>
                <div v-else-if="entry.status === 'INACTIVE'"> INACTIVE </div>
                <div v-else> ARCHIVED </div>
              </b-card-footer>
            </template>
          </content-card>
        </template>
        <template v-slot:pagination v-if="api && api.contentShareConnections && api.contentShareConnections.total">
          <b-pagination :per-page="filterLimit" :total-rows="api.contentShareConnections.total" v-model="filterPage"/>
        </template>
    </filter-container>

    <b-row v-if="showStepper" class="fixed-bottom border-top p-2 bg-white">
      <b-col>
        <b-btn class="float-left" variant="outline-primary" @click="backButton">
          Back
        </b-btn>
        <b-btn class="float-right" variant="primary" @click="saveAndNext">
          Next
        </b-btn>
      </b-col>
    </b-row>

    <!-- Edit Modal -->
    <template v-if="isRecruiter && selectedContentShareID && !hasContentEditorV2Enabled">
      <b-modal
        v-if="selectedContentShare && selectedContentShareID"
        id="editModal"
        visible
        size="xl"
        :title="contentShareEditorModalTitle"
        no-close-on-backdrop
        hide-footer
        @hidden="onShareModalClosed"
      >
        <error-block
          v-if="selectedContentShareErr"
          class="mb-3"
          :err="selectedContentShareErr"
        />
        <content-share-fetch
          :loading="$apollo.queries.prefetchedContentShare.loading"
          :prefetchError="prefetchError"
          :value="{
            url: prefetchURL || selectedContentShare.url,
            ...(prefetchedContentShare || (({ url, ...r }) => r)(selectedContentShare || {})),
          }"
          @fetchContent ="fetchContent($event)"
          @dismissPrefetchError="prefetchError = null"
          @input="updateContentShare"
          @disableSelectedContentShare="disableSelectedContentShare"
          @publishSelectedContentShare="publishSelectedContentShare"
          @deleteSelectedContentShare="deleteSelectedContentShare"
        />
      </b-modal>
      <b-modal
        v-else-if="selectedContentShareID === 'NEW'"
        visible
        size="xl"
        :title="contentShareEditorModalTitle"
        no-close-on-backdrop
        hide-footer
        @hidden="onShareModalClosed"
      >
        <error-block
          v-if="selectedContentShareErr"
          class="mb-3"
          :err="selectedContentShareErr"
        />
        <content-share-fetch
          :loading="$apollo.queries.prefetchedContentShare.loading"
          :prefetchError="prefetchError"
            :value="{
              url: prefetchURL,
              ...(prefetchedContentShare || {}),
            }"
          @fetchContent="fetchContent($event)"
          @dismissPrefetchError="prefetchError = null"
          @input="createNewContentShare"
        />
      </b-modal>
    </template>
  </div>
  </div>
</template>

<script>
import ms from 'ms';
import enumsMixin from '@src/mixins/enums';
import FilterContainer from '@/components/FilterContainer';
import ContentCard from '@/components/content-share/ContentCard';
import CampaignsContentSharesIndexQuery from '@src/graphql/queries/CampaignsContentSharesIndex.gql';
import FilterContentSharesWhere from '@src/components/campaign/FilterContentSharesWhere';
import scheduleLinkedInMixin from '@src/mixins/scheduleLinkedIn';
import OnboardingStepper from '@/components/profile/OnboardingStepper';
import ErrorBlock from '@/components/misc/ErrorBlock';
import ContentShareFetch from '@/components/content-share/ContentShareFetch';
import ContentShareEditor from '@/components/content-share/ContentShareEditor';
import ContentSharesListQuery from '@/graphql/queries/ContentSharesList.gql';
import PrefetchContentShareQuery from '@/graphql/queries/PrefetchContentShare.gql';
import ContentShareCreateMutation from '@/graphql/mutations/ContentShareCreate.gql';
import ContentShareUpdateMutation from '@/graphql/mutations/ContentShareUpdate.gql';
import { mapRoot } from '@src/mixins/root';

const { default: confetti } = process.env.VUE_ENV === 'client' ? require('canvas-confetti') : null;

export default {
  components: {
    FilterContainer,
    FilterContentSharesWhere,
    ContentCard,
    OnboardingStepper,
    ContentShareFetch,
    ContentShareEditor,
    ErrorBlock
  },
  props: {
    currentStep: {
      type: Object,
      default: () => ({
        name: 'campaign-content-share',
        displayName: 'Content',
        stepper: true
      }),
    },
    steps: {
      type: Array,
      default: () => [
        {
          name: 'company-setup',
          displayName: 'Company Branding',
          stepper: true
        },
        {
          name: 'campaign-jobs',
          displayName: 'Jobs',
          stepper: true
        },
        {
          name: 'campaign-content-share',
          displayName: 'Content',
          stepper: true
        },
        {
          name: 'event-settings',
          displayName: 'Event Settings',
          stepper: true
        }
      ],
    }
  },
  mixins: [
    enumsMixin,
    scheduleLinkedInMixin,
  ],
  data: () => ({
    err: null,
    filterDefaults: { status: 'ACTIVE' },
    filterWhere: {},
    filterPage: 1,
    filterLimit: 20,
    filterOrder: 'CREATED_DESC',
    showStepper: false,
    allContentSharesErr: null,
    selectedContentShareID: null,
    selectedContentShare: null,
    selectedContentShareErr: null,
    filterContentSharesPage: 1,
    filterContentSharesLimit: 12,
    filterContentSharesOrder: 'CREATED_DESC',
    prefetchedContentShare: null,
    prefetchURL: null,
    prefetchError: null,
  }),
  async mounted() {
    if (this.$route.query && this.$route.query.callback) {
      try {
        switch (this.$route.query.callback) {
          case 'connectedLinkedInForScheduling': {
            await this.connectedLinkedInForScheduling();
            this.$bvModal.show('schedule-campaign-content');
            break;
          }
        }
      } catch (err) {
        this.$log.error(err);
      }
    }
    console.log('Current Query: ', this.$route.query);
    if (this.$route && this.$route.query && this.$route.query.setup && this.$route.query.eventID) {
      this.showStepper = !this.showStepper;
    }
  },
  events: {
    capturedLog({ eventName }) {
      if ([ 'JOB_SHARED', 'CONTENT_SHARED' ].includes(eventName)) {
        confetti({ particleCount: 150, spread: 180, zIndex: 1200 });
        if (this.$apollo.queries.dashboard) {
          this.$apollo.queries.dashboard.refresh();
        }
      }
    },
  },
  apollo: {
    selectedContentShare: {
      query: ContentSharesListQuery,
      variables() {
        const { selectedContentShareID } = this;
        return {
          filterContentSharesWhere: { id: selectedContentShareID },
          filterContentSharesPage: 1,
          filterContentSharesLimit: 1,
        };
      },
      update: ({ company: { contentShareConnections: { edges: [ { entry } = {} ] = [] } = {} } = {} }) => entry,
      error(err) {
        this.selectedContentShareErr = err;
      },
      skip() {
        const { isRecruiter, selectedContentShareID } = this;
        return !isRecruiter || !selectedContentShareID || selectedContentShareID === 'NEW';
      },
      fetchPolicy: 'network-only',
    },
    prefetchedContentShare: {
      query: PrefetchContentShareQuery,
      variables() {
        const { prefetchURL } = this;
        return { url: prefetchURL };
      },
      manual: true,
      result({ data: { company }, loading }) {
        if (!loading) {
          const { contentSharePrefetch } = company || {};
          const { image, ...contentShare } = contentSharePrefetch || {};
          if (!contentShare.title || !contentShare.description) {
            this.prefetchError = this.$t('content-fetch.hints.unsuccessful-fetching');
          }
          this.prefetchedContentShare = { imageURL: image, ...contentShare };
        }
      },
      error(error) {
        this.prefetchError = error;
      },
      skip() {
        const { isRecruiter, prefetchURL } = this;
        return !isRecruiter || !prefetchURL;
      },
      fetchPolicy: 'network-only',
    },
    api: {
      query: CampaignsContentSharesIndexQuery,
      variables() {
        const {
          filterWhere: where,
          filterOrder: order,
          filterPage: page,
          filterLimit: limit,
        } = this;

        const whereDefaults = this.isRecruiter ? { OR: [ { status: 'ACTIVE' }, { status: 'INACTIVE' } ] } : this.filterDefaults;
        return { isEmployee: !this.isRecruiter, where: { ...whereDefaults, ...where }, order, page, limit };
      },
      update({ company, user }) {
        if (this.isRecruiter) {
          const { campaigns, contentShareConnections } = company;
          return { campaigns, contentShareConnections };
        } else {
          const { contentShareConnections } = company;
          const { campaigns } = user;
          return { campaigns, contentShareConnections };
        }
      },
      error(err) {
        this.$log.error(err);
      },
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
      deep: true,
      throttle: ms('1s'),
    },
  },
  metaInfo() {
    return {
      title: this.$t('contentShares.title'),
    };
  },
  computed: {
    ...mapRoot([ 'user', 'company', 'isLoggedIn', 'isRecruiter', 'isEnglishLang', 'isRecruiterAdmin' ]),
    contentShareEditorModalTitle() {
      const { selectedContentShareID: id } = this;
      return id ? (id === 'NEW' ? this.$t('content-fetch.titles.create-content') : this.$t('content-fetch.titles.edit-content')) : null;
    },
    canScheduleLinkedInPosts() {
      const { sitePreferences, preferences } = this.company || {};
      const { canScheduleLinkedInPosts } = { ...sitePreferences, ...preferences };
      return canScheduleLinkedInPosts === true;
    },
    orderByEnums: () => ({
      'CREATED_DESC': 'Newest first',
      'CREATED_ASC': 'Oldest first',
      'UPDATED_DESC': 'Recently updated',
      'UPDATED_ASC': 'Least updated',
    }),
    sitePreferences() {
      const { sitePreferences } = this.company || {};
      return sitePreferences || {};
    },
    hasContentEnabled() {
      const { featuresEnabled } = this.sitePreferences || {};
      return (featuresEnabled || []).includes('content');
    },
    hasContentEditorV2Enabled() {
      const { featuresEnabled } = this.sitePreferences || {};
      return (featuresEnabled || []).includes('content-editor-v2');
    },
  },
  methods: {
    newContentCreated(contentShare) {
      this.$bvModal.hide('new-content-modal');
      this.$apollo.queries.api.refetch();
    },
    contentUpdated(contentShare) {
      this.$bvModal.hide('edit-content-modal');
      this.$apollo.queries.api.refetch();
    },
    openEditModal(entryID) {
      this.selectedContentShareID = entryID;
      const { hasContentEditorV2Enabled } = this;
      if (!hasContentEditorV2Enabled) {
        this.$bvModal.show('editModal');
      } else {
        this.$bvModal.show('edit-content-modal');
      }
    },
    trackShareEvent({ id: contentShareID }, { channel }) {
      return this.$root.captureLog({
        eventName: 'CONTENT_SHARED',
        contentShare: { id: contentShareID },
        user: { self: true },
        data: channel ? [ { key: 'channel', value: channel } ] : null,
      });
    },
    filterWhereUpdated(where) {
      if (JSON.stringify(this.filterWhere) !== JSON.stringify(where)) {
        this.filterWhere = where;
        this.filterPage = 1;
        this.$scrollTo('body');
      }
    },
    filterOrderUpdated(order) {
      this.filterOrder = order;
      this.filterPage = 1;
      this.$scrollTo('body');
    },
    filterPageUpdated(page) {
      this.filterPage = page;
      this.$scrollTo('body');
    },
    backButton() {
      this.$router.push(
        {
          name: 'CampaignJobIndex',
          query: { setup: true, eventID: this.$route.query.eventID }
        }
      );
    },
    saveAndNext() {
      this.$router.push({
        name: 'EventSettings',
        params: { eventID: this.$route.query.eventID },
        query: { setup: true }
      });
    },
    onShareModalClosed() {
      this.prefetchURL = null;
      this.prefetchedContentShare = null;
      this.selectedContentShareID = null;
      this.selectedContentShare = null;
      this.selectedContentShareErr = null;
    },
    async createNewContentShare(create) {
      try {
        await this.$apollo.mutate({
          mutation: ContentShareCreateMutation,
          variables: { create },
        });

        await this.$apollo.queries.api.refetch();
        this.onShareModalClosed();
      } catch (err) {
        console.log(err);
        this.selectedUserErr = err;
      }
    },
    async updateContentShare(update) {
      const { selectedContentShareID: contentShareID } = this;
      if (!contentShareID) {
        return this.$notifyErr(new Error('Missing selectedContentShareID'));
      }

      try {
        const { data } = await this.$apollo.mutate({
          mutation: ContentShareUpdateMutation,
          variables: { contentShareID, update },
        });

        if (data && data.updateContentShare && data.updateContentShare.id) {
          const { title } = data.updateContentShare;
          this.$notification({
            title: 'Content updated',
            message: `Content with title '${title}' is updated successfully`,
            variant: 'success',
          });
        } else {
          throw new Error('Failed to update content');
        }

        await this.$apollo.queries.api.refetch();
        await this.onShareModalClosed();
      } catch (err) {
        console.error(err);
        this.selectedUserErr = err;
      }
    },
    async updateStatus(id, status) {
      try {
        const { data } = await this.$apollo.mutate({
          mutation: ContentShareUpdateMutation,
          variables: { contentShareID: id, update: { status } },
        });

        if (data && data.updateContentShare && data.updateContentShare.id) {
          const { title } = data.updateContentShare;
          this.$notification({
            title: 'Content updated',
            message: `Content with title '${title}' is updated successfully`,
            variant: 'success',
          });
        } else {
          throw new Error('Failed to update content');
        }

        await this.$apollo.queries.api.refetch();
      } catch (err) {
        console.error(err);
        this.selectedUserErr = err;
      }
    },
    async disableSelectedContentShare() {
      const { selectedContentShareID: contentShareID } = this;
      if (!contentShareID) {
        return this.$notifyErr(new Error('Missing selectedContentShareID'));
      }

      try {
        const { data } = await this.$apollo.mutate({
          mutation: ContentShareUpdateMutation,
          variables: { contentShareID, update: { status: 'INACTIVE' } },
        });

        if (data && data.updateContentShare && data.updateContentShare.id) {
          const { title } = data.updateContentShare;
          this.$notification({
            title: 'Content is archived',
            message: `Content with title '${title}' is archived`,
            variant: 'success',
          });
        } else {
          throw new Error('Failed to archive content');
        }

        await this.$apollo.queries.api.refetch();
        await this.onShareModalClosed();
      } catch (err) {
        console.error(err);
        this.selectedUserErr = err;
      }
    },
    async publishSelectedContentShare() {
      const { selectedContentShareID: contentShareID } = this;
      if (!contentShareID) {
        return this.$notifyErr(new Error('Missing selectedContentShareID'));
      }

      try {
        const { data } = await this.$apollo.mutate({
          mutation: ContentShareUpdateMutation,
          variables: { contentShareID, update: { status: 'ACTIVE' } },
        });

        if (data && data.updateContentShare && data.updateContentShare.id) {
          const { title } = data.updateContentShare;
          this.$notification({
            title: 'Content is published',
            message: `Content with title '${title}' is published`,
            variant: 'success',
          });
        } else {
          throw new Error('Failed to publish content');
        }

        await this.$apollo.queries.api.refetch();
        await this.onShareModalClosed();
      } catch (err) {
        console.error(err);
        this.selectedUserErr = err;
      }
    },
    async deleteSelectedContentShare() {
      const { selectedContentShareID: contentShareID } = this;
      if (!contentShareID) {
        return this.$notifyErr(new Error('Missing selectedContentShareID'));
      }

      try {
        await this.$apollo.mutate({
          mutation: ContentShareUpdateMutation,
          variables: { contentShareID, update: { status: 'DELETED' } },
        });

        this.$notification({
          title: 'Content is deleted',
          message: 'Content is successfully deleted',
          variant: 'success',
        });

        await this.$apollo.queries.api.refetch();
        await this.onShareModalClosed();
      } catch (err) {
        console.error(err);
        this.selectedUserErr = err;
      }
    },
    async fetchContent(url) {
      this.prefetchURL = null;
      if (url === this.prefetchURL) {
        await this.$apollo.queries.prefetchedContentShare.refetch();
        return;
      }
      this.prefetchURL = url;
    }
  },
};
</script>



// WEBPACK FOOTER //
// src/views/content-share/CampaignContentShareIndex.vue